import {sequenceT} from 'fp-ts/lib/Apply';
import {fromOption} from 'fp-ts/lib/Either';
import {} from 'fp-ts/lib/Foldable';
import {flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as C from 'fp-ts/lib/Console';
import {IOEither} from 'fp-ts/lib/IOEither';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import jwtDecode from 'jwt-decode';
import nanoid from 'nanoid';
import {
  fromLocalStorage,
  LocalStorageKey,
  removeFromLocalStorage,
  toLocalStorage,
  withValueSavedToLocalStorage,
} from 'Shared/utilities/localStorage';
import {parseBoolean, safeParseInt, safeParseJSONAsAny, safeParseJson} from 'Shared/utilities/parsers';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {UserClaims} from 'Users/models/Claims';

const getRandomId: IO.IO<string> = nanoid;
const generateNonce = withValueSavedToLocalStorage(getRandomId)(LocalStorageKey.Nonce);
const generateState = withValueSavedToLocalStorage(getRandomId)(LocalStorageKey.State);
export const redirect = (location: string) => () => window.location.assign(location);
const getLoginUrl = (state: string) => (nonce: string) => (isamPageLocation: string) =>
  `${isamPageLocation}&nonce=${nonce}&state=${state}`;

export const loginUrlWithStateAndNonce = pipe(IO.io.of(getLoginUrl), IO.ap(generateState), IO.ap(generateNonce));

export const generateLoginUrl = pipe(
  loginUrlWithStateAndNonce,
  IO.ap(() => process.env.REACT_APP_HOSTED_LOGIN_PAGE!)
);

export const generateIALoginUrl = pipe(
  loginUrlWithStateAndNonce,
  IO.ap(() => process.env.REACT_APP_HOSTED_LOGIN_PAGE_IA!)
);

export const login = IO.io.chain(generateLoginUrl, redirect);
export const loginIA = IO.io.chain(generateIALoginUrl, redirect);

export const getIsUserEmployee = pipe(
  fromLocalStorage(LocalStorageKey.IsEmployee),
  IO.map(O.fromNullable),
  IO.map(O.map(parseBoolean))
);

export const getLoginUrlByUserType = pipe(
  getIsUserEmployee,
  IO.map(O.fold(generateLoginUrl, isEmployee => (isEmployee ? generateLoginUrl() : generateIALoginUrl())))
);

export const refreshToken = () =>
  new Promise(resolve => {
    const isamFrame = document.createElement('iframe');
    isamFrame.setAttribute('src', getLoginUrlByUserType());
    isamFrame.setAttribute('id', 'authorization-iframe');
    isamFrame.setAttribute('class', 'authorization-iframe');

    document.body.appendChild(isamFrame);

    window.addEventListener('message', e => {
      if (e.origin === window.location.origin && e.data === 'timer') {
        resolve();
      }
    });
  });

export type Token = {
  sub?: string;
  Roles?: string;
  exp: number;
};

export const getUserName = pipe(
  fromLocalStorage(LocalStorageKey.AccessToken),
  IO.map(O.fromNullable),
  IO.map(O.chain(value => O.fromNullable<Token>(jwtDecode(value)))),
  IO.map(O.chain(token => O.fromNullable(token.sub)))
);

export const decodeMyAccessClaims = flow(
  (decodedToken: Token) => decodedToken.Roles,
  O.fromNullable,
  O.chain(safeParseJson<UserClaims>()),
  O.map(userClaims => ({...userClaims, key: 'UserClaims'}))
);

export const decodeToken = (accessToken: string): O.Option<Token> => O.tryCatch(() => jwtDecode(accessToken));

export const saveAccessToken = (accessToken: string, expiresIn: number, decodedToken: Token) => {
  const expiresAt = decodedToken.exp * 1000;
  const decodedMyAccessClaims = decodeMyAccessClaims(decodedToken);

  return sequenceT(IO.io)(
    toLocalStorage(LocalStorageKey.AccessToken)(accessToken),
    toLocalStorage(LocalStorageKey.ExpiresAt)(expiresAt),
    pipe(
      decodedMyAccessClaims,
      O.fold(
        () => C.log('No MyAccess claims found.'),
        myAccessClaims => toLocalStorage('claims')(JSON.stringify(myAccessClaims))
      )
    )
  );
};

export const clearAccessTokenFromLocalStorage = sequenceT(IO.io)(
  removeFromLocalStorage('access_token'),
  removeFromLocalStorage('id_token'),
  removeFromLocalStorage('expires_at'),
  removeFromLocalStorage('claims'),
  removeFromLocalStorage('nonce'),
  removeFromLocalStorage('state'),
  removeFromLocalStorage(LocalStorageKey.IsEmployee)
);

const clearSessionFromIsam = () => {
  const isamFrame = document.createElement('iframe');
  isamFrame.setAttribute('src', process.env.REACT_APP_LOGOUT_URL!);
  isamFrame.setAttribute('id', 'logout-iframe');
  isamFrame.setAttribute('class', 'logout-iframe');

  document.body.appendChild(isamFrame);
  isamFrame.onload = () => {
    if (window.frameElement) {
      window.frameElement.remove();
    }
  };
};

const clearIASessionFromIsam = () => {
  const isamFrame = document.createElement('iframe');
  isamFrame.setAttribute('src', process.env.REACT_APP_IA_LOGOUT_URL!);
  isamFrame.setAttribute('id', 'logout-iframe');
  isamFrame.setAttribute('class', 'logout-iframe');

  document.body.appendChild(isamFrame);
  isamFrame.onload = () => {
    if (window.frameElement) {
      window.frameElement.remove();
    }
  };
};

export const clearUserSession = sequenceT(IO.io)(
  pipe(
    fromLocalStorage(LocalStorageKey.IsEmployee),
    IO.map(value => !value || parseBoolean(value)),
    IO.chain(isEmployee => (isEmployee ? clearSessionFromIsam : clearIASessionFromIsam))
  ),
  clearAccessTokenFromLocalStorage
);

export const logoutFromAWD = sequenceT(IO.io)(
  removeFromLocalStorage(LocalStorageKey.AlertsDismissed),
  removeFromLocalStorage(LocalStorageKey.AWDDSTSession),
  removeFromLocalStorage(LocalStorageKey.AWDCsrfToken)
);

export const logout = sequenceT(IO.io)(clearUserSession, logoutFromAWD);

export const hasAcceptedTerms = pipe(
  getUserClaims,
  IO.map(
    O.fold(
      () => false,
      claims => (claims as UserClaims).hasAcceptedTermsAndConditions
    )
  )
);

export const isAuthenticated = pipe(
  fromLocalStorage(LocalStorageKey.ExpiresAt),
  IO.map(O.fromNullable),
  IO.map(
    flow(
      O.chain(safeParseJSONAsAny),
      O.map(expiresAt => {
        const hasNotExpired = new Date().getTime() < expiresAt;

        return hasNotExpired;
      })
    )
  ),
  IO.map(O.getOrElse(() => false))
);

export const getAccessToken = pipe(fromLocalStorage(LocalStorageKey.AccessToken), IO.map(O.fromNullable));

export const getExpiresAtTime: IOEither<string, number> = () => {
  const result = pipe(
    fromLocalStorage(LocalStorageKey.ExpiresAt),
    IO.map(O.fromNullable),
    IO.map(O.chain(safeParseInt)),
    IO.map(fromOption(() => 'User session has expired due to inactivity'))
  );
  return result();
};
